'use client';

import { useEffect, useRef, useState } from 'react';

import { SpeakerIcon } from '@/components/primitives/icons/SpeakerIcon';
import { GA_EVENTS, GA_PARAMS, type GaAudioId } from '@/constants/analytics';
import { trackEvent } from '@/lib/analytics/trackEvent';

import styles from './AudioButton.module.css';

type AudioButtonProps = {
  /** Audio file URL, e.g. `/audio/arctic-tern.mp3`. */
  src: string;
  /** Visible label, e.g. the bird name. */
  label: string;
  /** Stable GA4 `audio_id` when playback starts. */
  audioId?: GaAudioId;
  className?: string;
};

/**
 * Module-level registry so only one sound bite plays at a time —
 * starting one AudioButton pauses whichever other one was playing.
 */
let currentlyPlaying: HTMLAudioElement | null = null;

/**
 * Click-to-play sound bite. Never autoplays; `preload="metadata"` only.
 * Toggle state is exposed via `aria-pressed`. If the file fails to load
 * (real audio lands in task 17) the button disables itself gracefully.
 */
export function AudioButton({ src, label, audioId, className }: AudioButtonProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasTrackedPlayRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    hasTrackedPlayRef.current = false;
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio && currentlyPlaying === audio) {
        currentlyPlaying = null;
      }
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      return;
    }

    if (currentlyPlaying && currentlyPlaying !== audio) {
      currentlyPlaying.pause();
    }

    audio
      .play()
      .then(() => {
        currentlyPlaying = audio;
      })
      .catch(() => {
        // Missing/unloadable file (dev preview) — disable rather than crash.
        setFailed(true);
        setPlaying(false);
      });
  };

  return (
    <button
      type="button"
      className={[styles.root, className].filter(Boolean).join(' ')}
      aria-pressed={playing}
      data-playing={playing || undefined}
      disabled={failed}
      onClick={toggle}
    >
      <span className={styles.label}>{label}</span>
      <SpeakerIcon size={24} className={styles.icon} />
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => {
          setPlaying(true);
          if (audioId && !hasTrackedPlayRef.current) {
            hasTrackedPlayRef.current = true;
            trackEvent(GA_EVENTS.audioPlay, {
              [GA_PARAMS.audioId]: audioId,
            });
          }
        }}
        onPause={() => setPlaying(false)}
        onError={() => setFailed(true)}
      />
    </button>
  );
}
